import { supabase } from "./supabase";

/**
 * resolveCanvasPinForLead — find-or-create a canvas knock pin for a manually
 * logged lead so the property shows up on the canvas map at the right house,
 * tied to its parcel, exactly like a canvassed lead.
 *
 * IMPORTANT ordering: the returned `pinId` MUST be written into the
 * `deals.knock_pin_id` column on the INITIAL deal INSERT — not a later UPDATE.
 * The advance trigger `trg_canvas_advance_pin_from_deal` early-returns on an
 * UPDATE when the stage is unchanged (`OLD.stage IS NOT DISTINCT FROM
 * NEW.stage`), so a knock_pin_id-only UPDATE never advances the pin. On INSERT
 * the trigger fires, sees stage `pending_asm_review` (in its "appt" group), and
 * advances the pin to `appt_scheduled`, back-linking knock_pins.deal_id.
 * (Verified via rolled-back DB smoke test, Jun 17 2026.)
 *
 * Flow:
 *   1. Resolve the parcel at the geocoded coords (canvas_nearest_parcel_for,
 *      100m — same radius the Canvas drop flow uses).
 *   2. If a pin already exists on that parcel → REUSE it (avoids the stacked-pin
 *      problem the May 20 dedupe cleanup fixed).
 *   3. Otherwise INSERT a fresh pin. `trg_knock_pins_attach_parcel` (BEFORE
 *      INSERT) auto-stamps parcel_id + canonical street/city/state/zip from the
 *      parcel row; current_state defaults to 'open'.
 *
 * Best-effort: every step is guarded; returns null on any failure (no actorId,
 * a role that can't write knock_pins, network, etc.) so the lead still saves
 * without a pin. `actorId` is required because knock_pins.dropped_by is NOT NULL.
 *
 * On a caller-side failure AFTER this resolves (e.g. the deal insert throws),
 * pass the freshly-created pin id to `deleteOrphanPin` to avoid leaving an
 * unlinked 'open' pin behind.
 */

export type ResolvedPin = {
  pinId: string;
  reused: boolean;
  parcelId: string | null;
};

export async function resolveCanvasPinForLead(opts: {
  lat: number;
  lon: number;
  street: string;
  city: string;
  state: string;
  zip: string | null;
  homeownerName: string | null;
  actorId: string | null;
}): Promise<ResolvedPin | null> {
  const { lat, lon, street, city, state, zip, homeownerName, actorId } = opts;
  if (!actorId) return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

  try {
    // 1) Resolve the parcel at these coords (null when outside loaded counties).
    let parcelId: string | null = null;
    try {
      const { data: pid } = await supabase.rpc("canvas_nearest_parcel_for", {
        p_lat: lat,
        p_lon: lon,
        p_max_meters: 100,
      });
      if (pid) parcelId = pid as string;
    } catch {
      /* no parcel match — fall through to create a fresh pin */
    }

    // 2) Reuse an existing pin on that parcel if one is already there.
    if (parcelId) {
      const { data: existing } = await supabase
        .from("knock_pins")
        .select("id")
        .eq("parcel_id", parcelId)
        .order("dropped_at", { ascending: false })
        .limit(1);
      if (existing && existing.length > 0) {
        return {
          pinId: (existing[0] as { id: string }).id,
          reused: true,
          parcelId,
        };
      }
    }

    // 3) No pin yet → create one. BEFORE trigger stamps parcel_id + canonical
    //    address; current_state defaults to 'open' (a valid prev-state for the
    //    deal trigger to advance to appt_scheduled).
    const { data: created, error: insErr } = await supabase
      .from("knock_pins")
      .insert({
        property_street: street.trim(),
        property_city: city.trim(),
        property_state: (state.trim() || "GA").toUpperCase(),
        property_zip: zip?.trim() || null,
        lat,
        lon,
        homeowner_name: homeownerName?.trim() || null,
        dropped_by: actorId,
        notes: "📝 Manual lead entry",
      })
      .select("id")
      .single();
    if (insErr || !created) return null;
    return {
      pinId: (created as { id: string }).id,
      reused: false,
      parcelId,
    };
  } catch {
    return null;
  }
}

/** Best-effort cleanup of a freshly-created pin when the deal insert fails. */
export async function deleteOrphanPin(pinId: string): Promise<void> {
  try {
    await supabase.from("knock_pins").delete().eq("id", pinId);
  } catch {
    /* leave it — a stray unlinked pin is harmless */
  }
}
